import React, { useEffect, useRef, useState } from "react";

import clsx from "clsx";
import { UseFieldArrayMove, UseFormGetValues, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';

import { ArrowDownIcon, ArrowUpIcon, PaperAirplaneIcon, TrashIcon } from "@heroicons/react/outline";

import { BaseCard } from "./BaseCard";
import TinyMCE from "../Shared/TinyMCE";
import StyledSwitchInput from "../Shared/StyledInputs/StyledSwitchInput";
import { StyledTextInput } from "../Shared/StyledInputs/StyledTextInput";
import { StyledSelectInput } from "../Shared/StyledInputs/StyledSelectInput";
import { StyledRadioButtonGroupInput } from "../Shared/StyledInputs/StyledRadioButtonGroupInput";
import { Editor } from "@tinymce/tinymce-react";
import ConfirmDelete from "../Shared/ConfirmDelete";
import { convertSubsToNested } from "../../utils/convertSubsToMergeTags";
import { SubstitutionDropdownButton } from "../Shared/SubstitutionDropdownButton";
import { ButtonInput } from "../Shared/Inputs/ButtonInput";

interface RuleCardProps {
  ruleIndex: number;
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  getValues: UseFormGetValues<any>;
  watch: UseFormWatch<any>;
  onDelete: (ruleIndex: number) => void;
  move: UseFieldArrayMove;
  isFirst: boolean;
  isLast: boolean;
  errors?: any;
  onSubmitCampaign?: () => Promise<any>;
  onOpenSendTest?: (ruleIndex: number) => void;
}

export function RuleCard({
  ruleIndex,
  register,
  setValue,
  getValues,
  watch,
  onDelete,
  move,
  isFirst,
  isLast,
  errors,
  onSubmitCampaign,
  onOpenSendTest,
}: RuleCardProps) {
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const editorRef = useRef<Editor | null>(null);

  const getPredefinedUrl = (path: string) => `{{agency.Domain}}/${path}`;

  const predefinedActions = [
    { label: "Valuation", value: getPredefinedUrl("valuation") },
    { label: "Book a Valuation", value: getPredefinedUrl("book-a-valuation") },
    { label: "Properties", value: getPredefinedUrl("properties") },
    { label: "For Sale Properties", value: getPredefinedUrl("properties/for-sale") },
    { label: "To Rent Properties", value: getPredefinedUrl("properties/to-rent") },
    { label: "Sold Properties", value: getPredefinedUrl("sold-properties") },
    { label: "Let Properties", value: getPredefinedUrl("let-properties") },
    { label: "Galleries", value: getPredefinedUrl("galleries") },
    { label: "Area Guide", value: getPredefinedUrl("area-guide") },
    { label: "Testimonials", value: getPredefinedUrl("testimonials") },
    { label: "News", value: getPredefinedUrl("news") },
    { label: "Custom URL", value: "CUSTOM_URL" }
  ];

  const baseMergeTags =
    [
      { label: "First Name", value: "person.firstName" },
      { label: "Last Name", value: "person.lastName" },
      { label: "Email Address", value: "person.Email" },
      { label: "Name", value: "agency.Name" },
      { label: "Domain", value: "agency.Domain" },
      { label: "Telephone Number", value: "agency.TelephoneNumber" }
    ]

  const getAdditionalMergeTags = (trigger: string) => {
    switch (trigger) {
      case "1":
        return [
          { label: "Type", value: "valuation.Type" },
          { label: "House Number", value: "valuation.HouseNumber" },
          { label: "Street", value: "valuation.Street" },
          { label: "Town", value: "valuation.Town" },
          { label: "Postcode", value: "valuation.Postcode" },
          { label: "Formatted Address", value: "valuation.AddressFormatted" },
          { label: "Bedrooms", value: "valuation.NoBedrooms" },
          { label: "Bathrooms", value: "valuation.NoBathrooms" },
          { label: "Looking To Move", value: "valuation.LookingToMove" },
          { label: "Sale Value", value: "valuation.SaleValue" },
          { label: "Sale Value (Min)", value: "valuation.SaleValueMin" },
          { label: "Sale Value (Max)", value: "valuation.SaleValueMax" },
          { label: "Rental Value", value: "valuation.RentalValue" },
          { label: "Rental Value (Min)", value: "valuation.RentalValueMin" },
          { label: "Rental Value (Max)", value: "valuation.RentalValueMax" },
          { label: "Appointment Time and Date", value: "valuation.AppointmentDateTime" },
          { label: "Appointment Date Only", value: "valuation.AppointmentDate" },
          { label: "Appointment Time Only", value: "valuation.AppointmentTime" },
        ];
      case "2":
        return [
          { label: "Property Type", value: "registration.propertyType" },
          { label: "Min Price", value: "registration.minPrice" },
          { label: "Max Price", value: "registration.maxPrice" },
          { label: "Min Bedrooms", value: "registration.minBedrooms" },
          { label: "Max Bedrooms", value: "registration.maxBedrooms" },
          { label: "Location", value: "registration.location" }
        ];
      case "3":
        return [
          { label: "Telephone Number", value: "form.telephoneNumber" },
          { label: "Source", value: "form.source" },
          { label: "Follow Up Date", value: "form.followUpDate" }
        ];
      case "4":
        return [
          { label: "Title", value: "property.Title" },
          { label: "Name/Number", value: "property.AddressNameNumber" },
          { label: "Street", value: "property.AddressStreet" },
          { label: "Line 2", value: "property.AddressTwo" },
          { label: "Line 3", value: "property.AddressThree" },
          { label: "Line 4", value: "property.AddressFour" },
          { label: "Postcode", value: "property.AddressPostcode" },
          { label: "Formatted Address", value: "property.AddressFormatted" },
          { label: "Price", value: "property.Price" },
          { label: "Type", value: "property.Type" },
          { label: "Appointment Date", value: "viewing.Date" },
          { label: "Appointment Time", value: "viewing.Time" }
        ];
      default:
        return [];
    }
  };

  const moveUpDisabled = isFirst;
  const moveDownDisabled = isLast;
  const trigger = watch("trigger");

  const handleSendTest = async () => {
    try {
      if (onSubmitCampaign) await onSubmitCampaign();
      onOpenSendTest && onOpenSendTest(ruleIndex);
    } catch (err) {
      console.error("Error submitting campaign before sending test:", err);
    }
  }

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.editor?.editorManager.setup();
    }
  }, [trigger])

  return (
    <>
      {/* @ts-ignore */}
      <ConfirmDelete
        itemName={getValues(`rules.${ruleIndex}.name`) || "New Rule"}
        open={showConfirmDelete}
        setOpen={setShowConfirmDelete}
        onConfirm={() => onDelete(ruleIndex)}
      />
      {/* Parent controls SendTestModal to avoid unmounting during saves */}
      <div>
        <BaseCard
          title={{
            whenExpanded: `Email #${ruleIndex + 1}`,
            whenCollapsed: `Email #${ruleIndex + 1} - ${getValues(`rules.${ruleIndex}.name`) || "New Rule"}`
          }}
          rightAdornment={
            <StyledSwitchInput
              label="Running"
              registerName={`rules.${ruleIndex}.enabled`}
              register={register}
              setValue={setValue}
              watch={watch}
            />
          }
          expandable
          initiallyExpanded={true}
        >
          <div>
            <div className="bg-gray-50 p-4 rounded-lg my-4">
              <div className="">
                <StyledTextInput
                  label="Name"
                  registerName={`rules.${ruleIndex}.name`}
                  register={register}
                  colsClass=""
                  description="A friendly name for this email, your customers won't see this"
                  error={errors?.rules?.[ruleIndex]?.name?.message}
                />
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg my-4 mt-2">
              <div className="w-full flex flex-row items-end space-x-2">
                <div className="w-full flex flex-col">
                  <StyledTextInput
                    label="Subject"
                    registerName={`rules.${ruleIndex}.subject`}
                    register={register}
                    classes="w-full"
                    colsClass=""
                    error={errors?.rules?.[ruleIndex]?.subject?.message}
                  />
                </div>
                <div className="mb-0.5">
                  <SubstitutionDropdownButton
                    substitutions={baseMergeTags}
                    onSelect={(value) => {
                      const current = getValues(`rules.${ruleIndex}.subject`) || "";
                      const newValue = current + `{{${value}}}`;
                      setValue(`rules.${ruleIndex}.subject`, newValue);
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg my-4 mt-2">
              <div>
                <label className="text-sm font-medium text-gray-700">Content</label>
                <TinyMCE
                  ref={editorRef}
                  // @ts-ignore
                  height={300}
                  // @ts-ignore
                  value={getValues(`rules.${ruleIndex}.content`)}
                  // @ts-ignore
                  onChange={(content: any) => setValue(`rules.${ruleIndex}.content`, content, { shouldDirty: true })}
                  // @ts-ignore
                  mergeTags={convertSubsToNested([...baseMergeTags, ...getAdditionalMergeTags(watch(`trigger`))])}
                />
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg my-4">
              <div className="flex items-center space-x-2 justify-between">
                <p className="text-xs text-gray-500">If enabled a Call-To-Action button will appear between the content defined above and the email footer</p>
                <StyledSwitchInput
                  label="Enable Call to Action"
                  registerName={`rules.${ruleIndex}.enableButton`}
                  register={register}
                  setValue={setValue}
                  watch={watch}
                />
              </div>
              {watch(`rules.${ruleIndex}.enableButton`) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  <StyledTextInput
                    label="Button Text"
                    registerName={`rules.${ruleIndex}.buttonText`}
                    register={register}
                    error={errors?.rules?.[ruleIndex]?.buttonText?.message}
                  />
                  <StyledSelectInput
                    label="Choose Website Page"
                    registerName={`rules.${ruleIndex}.buttonUrl`}
                    register={register}
                    setValue={setValue}
                    watch={watch}
                    options={predefinedActions}
                    onChange={(value) => {
                      if (value === "CUSTOM_URL") {
                        setValue(`rules.${ruleIndex}.customUrl`, "", { shouldDirty: true });
                      }
                    }}
                  />
                  {watch(`rules.${ruleIndex}.buttonUrl`) === "CUSTOM_URL" && (
                    <StyledTextInput
                      label="Custom Button URL"
                      registerName={`rules.${ruleIndex}.customUrl`}
                      register={register}
                      error={errors?.rules?.[ruleIndex]?.customUrl?.message}
                    />
                  )}
                </div>
              )}
            </div>
            <div className="bg-gray-50 p-4 rounded-lg my-4">
              <h4 className="font-medium text-base">Delay</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 items-end space-x-4 mt-2">
                <StyledTextInput
                  label="Delay before sending this email"
                  registerName={`rules.${ruleIndex}.delay`}
                  register={register}
                  registerOptions={{
                    validate: value => {
                      if (value === "" || value === undefined) return "Delay is required";
                      const num = Number(value);
                      if (isNaN(num)) return "Delay must be a number";
                      if (num < 0) return "Delay must be 0 or more";
                      return true;
                    }
                  }}
                  error={errors?.rules?.[ruleIndex]?.delay?.message}
                />
                <StyledRadioButtonGroupInput
                  registerName={`rules.${ruleIndex}.delaySpan`}
                  options={[
                    { label: "Hours", value: "hours" },
                    { label: "Days", value: "days" },
                  ]}
                  setValue={setValue}
                  watch={watch}
                />
              </div>
            </div>
          </div>
        </BaseCard>
        <div className="flex flex-row items-center justify-between">
          <div className="space-x-4 pt-3">
            <button
              disabled={moveUpDisabled}
              type="button"
              onClick={() => move(ruleIndex, ruleIndex - 1)}
              className={clsx("rounded p-2",
                moveUpDisabled ?
                  "bg-gray-200 cursor-not-allowed"
                  : "bg-gray-100 hover:bg-gray-200"
              )}
            >
              <ArrowUpIcon className={clsx("h-4 w-4", moveUpDisabled ? "text-gray-300" : "text-gray-500")} />
              <span className="sr-only">Move Up</span>
            </button>
            <button
              disabled={moveDownDisabled}
              type="button"
              onClick={() => move(ruleIndex, ruleIndex + 1)}
              className={clsx("rounded p-2",
                moveDownDisabled ?
                  "bg-gray-200 cursor-not-allowed"
                  : "bg-gray-100 hover:bg-gray-200"
              )}
            >
              <ArrowDownIcon className={clsx("h-4 w-4", moveDownDisabled ? "text-gray-300" : "text-gray-500")} />
              <span className="sr-only">Move Down</span>
            </button>
          </div>
          <div className="space-x-4 pt-3">
            {/* @ts-ignore */}
            <ButtonInput
              label="Delete Email"
              onClick={() => setShowConfirmDelete(true)}
              classes="bg-red-600 hover:bg-red-700 text-white"
            />
            {/* @ts-ignore */}
            <ButtonInput
              label="Send Test Email"
              onClick={handleSendTest}
              classes="bg-green-600 hover:bg-green-700 text-white"
            />
          </div>
        </div>
      </div>
    </>
  );
}